import { useMemo } from 'react'

import type { FilaCartera } from '@/lib/supabase/tipos-bd'
import { pesos } from '@/shared/utilidades/formato'

import { debePasarAFev, EDADES, edadDe, type ClaveEdad } from './edades'
import estilos from './MatrizClientesEdades.module.css'

type Props = {
  cuentas: FilaCartera[]
  /** La edad elegida en la barra de arriba: las demás columnas se apagan. */
  edad: ClaveEdad | null
  onAbrir: (c: FilaCartera) => void
}

type Fila = {
  cliente: string
  total: number
  celdas: Record<ClaveEdad, FilaCartera[]>
  sumas: Record<ClaveEdad, number>
}

const enCero = () => ({ e0: 0, e1: 0, e2: 0, e3: 0 })
const cuantas = (f: Fila) => EDADES.reduce((n, e) => n + f.celdas[e.clave].length, 0)

/**
 * CLIENTES × EDADES (0316).
 *
 * Johan: «se debe mostrar clientes en la columna izquierda y edades en la
 * primera fila, y de ahí cruzar las ODT; todo se debería agrupar por ODT».
 * Cada cuenta de la cartera ya es una orden, así que cada cruce muestra las
 * órdenes de ese cliente que tienen esa edad, con lo que deben; tocar una abre
 * su tarjeta. Solo lo que se debe: una orden pagada no tiene edad de cobro.
 * La edad es la de siempre en la cartera: desde que se finalizó el servicio.
 */
export function MatrizClientesEdades({ cuentas, edad, onAbrir }: Props) {
  const { filas, totales } = useMemo(() => {
    const mapa = new Map<string, Fila>()
    const totales = { ...enCero(), total: 0 }
    for (const c of cuentas) {
      const saldo = Number(c.saldo)
      if (saldo <= 0) continue
      const cliente = c.cliente ?? 'Sin cliente'
      let f = mapa.get(cliente)
      if (!f) {
        f = { cliente, total: 0, celdas: { e0: [], e1: [], e2: [], e3: [] }, sumas: enCero() }
        mapa.set(cliente, f)
      }
      const e = edadDe(c.dias).clave
      f.celdas[e].push(c)
      f.sumas[e] += saldo
      f.total += saldo
      totales[e] += saldo
      totales.total += saldo
    }
    // El que más debe, arriba; dentro de cada cruce, la orden más vieja primero.
    const filas = [...mapa.values()].sort((a, b) => b.total - a.total)
    for (const f of filas) for (const e of EDADES) f.celdas[e.clave].sort((a, b) => b.dias - a.dias)
    return { filas, totales }
  }, [cuentas])

  if (filas.length === 0) {
    return <p className={estilos.vacio}>Nadie debe nada con este filtro.</p>
  }

  return (
    <div className={estilos.caja}>
      <table className={estilos.matriz}>
        <thead>
          <tr>
            <th scope="col" className={estilos.esquina}>
              Cliente <span>{filas.length}</span>
            </th>
            {EDADES.map((e) => (
              <th
                key={e.clave}
                scope="col"
                className={`${estilos[e.clave]} ${edad && edad !== e.clave ? estilos.apagada : ''}`}
              >
                <i aria-hidden="true" />
                {e.rotulo}
              </th>
            ))}
            <th scope="col" className={estilos.colTotal}>
              Debe
            </th>
          </tr>
        </thead>
        <tbody>
          {filas.map((f) => (
            <tr key={f.cliente}>
              <th scope="row">
                <span className={estilos.cliente}>{f.cliente}</span>
                <span className={estilos.clienteDetalle}>
                  {cuantas(f)} {cuantas(f) === 1 ? 'orden' : 'órdenes'}
                </span>
              </th>
              {EDADES.map((e) => (
                <td
                  key={e.clave}
                  className={`${estilos[e.clave]} ${f.celdas[e.clave].length ? estilos.llena : ''} ${
                    edad && edad !== e.clave ? estilos.apagada : ''
                  }`}
                >
                  {f.celdas[e.clave].map((c) => (
                    <button
                      key={c.orden_servicio_id}
                      type="button"
                      className={estilos.orden}
                      onClick={() => onAbrir(c)}
                      title={debePasarAFev(c) ? 'Tiene COT que hay que pasar a FEV' : undefined}
                    >
                      <span className={estilos.ordenNumero}>
                        {debePasarAFev(c) && <i className={estilos.alerta} aria-label="Por pasar a FEV" />}
                        {c.orden_trabajo ?? c.numero}
                      </span>
                      <span className={estilos.ordenSaldo}>{pesos(Number(c.saldo))}</span>
                      <span className={estilos.ordenDetalle}>
                        {c.orden_trabajo ? `${c.numero} · ` : ''}
                        {c.dias} d
                      </span>
                    </button>
                  ))}
                  {f.celdas[e.clave].length > 1 && (
                    <span className={estilos.suma}>{pesos(f.sumas[e.clave])}</span>
                  )}
                </td>
              ))}
              <td className={estilos.colTotal}>{pesos(f.total)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row">Total</th>
            {EDADES.map((e) => (
              <td key={e.clave} className={`${estilos[e.clave]} ${edad && edad !== e.clave ? estilos.apagada : ''}`}>
                {totales[e.clave] > 0 ? pesos(totales[e.clave]) : '—'}
              </td>
            ))}
            <td className={estilos.colTotal}>{pesos(totales.total)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  )
}
